import React, { useCallback, useMemo } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import Card from 'components/shared/Card';
import { TechnologyConflict } from 'store/types/Meeting';
import { ActivityType, IPCheckType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { meetingSessionToActivity } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

interface CopyrightedItemCardProps {
  item: TechnologyConflict;
  index: number;
  updateItem: (id: string, patch: Partial<TechnologyConflict>) => void;
  removeItem: (id: string) => void;
  displayConflict: (conflict: TechnologyConflict) => void;
  clearConflict: () => void;
}

const CopyrightedItemCard: React.FunctionComponent<CopyrightedItemCardProps> = ({
  item,
  index,
  updateItem,
  removeItem,
  displayConflict,
  clearConflict,
}) => {
  const { meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();

  const itemNumber = index + 1;

  const enableSave = useMemo(
    () => item.isUsed === QuestionResponse.No || item.isJustified,
    [item.isJustified, item.isUsed]
  );
  const displayedItem = useMemo(
    () => ({
      ...item,
      itemNumber,
    }),
    [item, itemNumber]
  );

  const handleSave = useCallback(async () => {
    logActivity(
      {
        ...meetingSessionToActivity(meetingSession),
        type: ActivityType.IpCheck,
        motion: meetingSession?.currentMotion,
        votingSession: meetingSession?.currentVotingSession,
        ipCheckSession: {
          type: IPCheckType.Affirmative,
          copyrightConflict: QuestionResponse.Yes,
        },
        technologyConflict: displayedItem,
      },
      'Saved Copyrighted Item'
    );
  }, [displayedItem, logActivity, meetingSession]);

  return (
    <Card
      title={<Typography variant="h6">Copyrighted Item #{itemNumber}</Typography>}
      bordered
      headerAction={
        <Button color={'error'} variant={'contained'} onClick={() => removeItem(item.id)}>
          Remove
        </Button>
      }
    >
      <GridContainer>
        <GridItem>
          <FormControl className="w-100">
            <FormLabel>Disclosed copyrighted item #{itemNumber}</FormLabel>
            <TextField
              label={'Brief description of its use in the Document'}
              value={item.description}
              required
              multiline
              onChange={(e) => updateItem(item.id, { description: e.target.value })}
            ></TextField>
          </FormControl>
        </GridItem>
        <GridItem className={commonStyles.buttonGroup}>
          <Button variant="contained" color="secondary" onClick={() => displayConflict(displayedItem)}>
            Display Copyrighted
          </Button>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel>
              Is disclosed copyrighted item #{itemNumber} used or reproduced in the Standard/Safety Guideline?
            </FormLabel>
            <RadioGroup
              value={item.isUsed ?? ''}
              onChange={(event) => {
                const value = event.target.value as QuestionResponse;
                if (value === item.isUsed) return;

                let patch: Partial<TechnologyConflict>;
                if (value === QuestionResponse.No) {
                  patch = { isUsed: value, isJustified: undefined };
                } else {
                  patch = { isUsed: value };
                }

                const nextItem = { ...displayedItem, ...patch };
                updateItem(item.id, patch);
                void displayConflict(nextItem);
              }}
            >
              <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
              <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
            </RadioGroup>
          </FormControl>
        </GridItem>
        {item.isUsed === QuestionResponse.No && (
          <GridItem>No further action is needed for copyrighted item #{itemNumber}</GridItem>
        )}
        {item.isUsed === QuestionResponse.Yes && (
          <GridItem>
            <FormControl>
              <FormLabel>Is the use/reproduction of this copyrighted item technically justified?</FormLabel>
              <RadioGroup
                value={item.isJustified ?? ''}
                onChange={(event) => {
                  const value = event.target.value as QuestionResponse;
                  if (value === item.isJustified) return;

                  const patch = { isJustified: value };
                  const nextItem = { ...displayedItem, ...patch };
                  updateItem(item.id, patch);
                  void displayConflict(nextItem);
                }}
              >
                <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
                <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
              </RadioGroup>
            </FormControl>
          </GridItem>
        )}
        {item.isJustified === QuestionResponse.Yes && (
          <GridItem>
            <Typography>
              {/* PROCEED to assess NEXT one, or if this is the last one, GO TO Copyright Release Status */}
            </Typography>
          </GridItem>
        )}
        {item.isJustified === QuestionResponse.No && (
          <GridItem>
            <Typography>The Document is failed and returned to the TF</Typography>
          </GridItem>
        )}
        <GridItem className={commonStyles.buttonGroup}>
          <Button variant="contained" color="secondary" disabled={!enableSave} onClick={handleSave}>
            Save
          </Button>
          <Button variant="contained" color="error" onClick={clearConflict}>
            Clear Copyrighted
          </Button>
        </GridItem>
      </GridContainer>
    </Card>
  );
};

export default CopyrightedItemCard;
